import { CYLINDER_FILTERS as T } from '@locales/id/cylinders';
import { FILL_STATE_LABEL, locationTypeLabel, LOCATION_TYPE } from '@locales/id/labels';
import { COMMON } from '@locales/id/messages';
import { FILL_STATES, LOCATION_TYPES, NOT_SEEN_FOR, TEST_FILTERS, type FillState, type LocationType, type NotSeenFor, type TestFilter } from '@lib/api';
import { Icon, MenuCheck, MenuChoice } from '@ui';
import type { PlaceSearch } from '@features/cylinders/model/usePlaceSearch';

/** "Kosongkan pilihan" under a checklist, once something is checked. */
function ClearChoice({ shown, onClear }: { shown: boolean; onClear: () => void }) {
  if (!shown) return null;
  return (
    <>
      <hr className="fmenu-sep" />
      <button type="button" className="fmenu-option fmenu-clear" data-menu-item onClick={onClear}>
        {T.clearChoice}
      </button>
    </>
  );
}

/**
 * Titik Distribusi: the kinds of place (any number; none = all, it replaces the old "Semua area"), then one place by
 * searching its name or code. The list narrows to the chosen region and kinds.
 */
export function PlacePanel({ types, onToggleType, onClearTypes, locationId, search, onPick, autoFocus }: {
  types: LocationType[];
  onToggleType: (t: LocationType) => void;
  onClearTypes: () => void;
  locationId: number | null;
  search: PlaceSearch;
  onPick: (id: number | null, name?: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <>
      <p className="fmenu-heading">{T.placeType}</p>
      <div className="fmenu-list cyl-type-list" role="group" aria-label={T.placeType}>
        {LOCATION_TYPES.map((t) => (
          <MenuCheck key={t} checked={types.includes(t)} onToggle={() => onToggleType(t)}>
            {LOCATION_TYPE[t]}
          </MenuCheck>
        ))}
        <ClearChoice shown={types.length > 0} onClear={onClearTypes} />
      </div>
      <hr className="fmenu-sep" />
      <p className="fmenu-heading">{T.place}</p>
      <PlaceSearchList locationId={locationId} search={search} onPick={onPick} autoFocus={autoFocus} />
    </>
  );
}

/**
 * One Titik Distribusi by searching its name or code (the network has thousands: a server search, the first few
 * listed), "Semua Titik Distribusi" first while nothing is typed. While the list is not yet for what the box says it
 * is dimmed with a spinner, so nobody picks from stale rows. Shared with the Surat Jalan list's place menu.
 */
export function PlaceSearchList({ locationId, search, onPick, autoFocus }: { locationId: number | null; search: PlaceSearch; onPick: (id: number | null, name?: string) => void; autoFocus?: boolean }) {
  const { q, setQ, list, stale } = search;
  const typed = q.trim() !== '';
  const places = list.data?.items ?? [];
  const checkedShown = (!typed && locationId === null) || places.some((p) => p.id === locationId);
  const more = (list.data?.meta.total ?? 0) > places.length;
  return (
    <>
      <label className="fmenu-search">
        <Icon name="search" size={16} />
        <span className="sr-only">{T.placeSearch}</span>
        <input type="search" value={q} placeholder={T.placeSearch} onChange={(e) => setQ(e.target.value)} data-menu-item data-autofocus={autoFocus || undefined} autoComplete="off" />
        {stale && <span className="spinner" aria-hidden="true" />}
      </label>
      <span className="sr-only" role="status">{stale && typed ? T.searching : ''}</span>
      <div className={`fmenu-list fmenu-scroll ${stale && list.data ? 'is-stale' : ''}`} role="radiogroup" aria-label={T.place} aria-busy={stale || undefined}>
        {!typed && (
          <MenuChoice checked={locationId === null} onSelect={() => onPick(null)}>
            {T.allPlaces}
          </MenuChoice>
        )}
        {places.map((p, i) => (
          <MenuChoice key={p.id} checked={p.id === locationId} focusable={!checkedShown && i === 0} sub={`${locationTypeLabel(p.type)} · ${p.regencyName ?? p.code}`} onSelect={() => onPick(p.id, p.name)}>
            {p.name}
          </MenuChoice>
        ))}
      </div>
      {list.error && !list.loading ? (
        <div className="fmenu-state" role="alert">
          <span>{T.placesFailed}</span>
          <button type="button" className="btn btn-sm" onClick={list.retry}>{COMMON.retry}</button>
        </div>
      ) : list.data && !stale && !places.length ? (
        <p className="fmenu-state" role="status">{T.placesEmpty}</p>
      ) : !list.data && list.loading ? (
        <p className="fmenu-state" role="status">{COMMON.loading}</p>
      ) : more ? (
        <p className="fmenu-state xs">{T.morePlaces(places.length)}</p>
      ) : null}
    </>
  );
}

/** Uji berkala: all, past, or due within the retest window (`days`). */
export function TestPanel({ value, days, onPick }: { value: Exclude<TestFilter, 'due'> | ''; days: number; onPick: (v: Exclude<TestFilter, 'due'> | '') => void }) {
  return (
    <div className="fmenu-list" role="radiogroup" aria-label={T.test}>
      <MenuChoice checked={!value} onSelect={() => onPick('')}>
        {T.allTests}
      </MenuChoice>
      {TEST_FILTERS.map((t) => (
        <MenuChoice key={t} checked={value === t} onSelect={() => onPick(t)}>
          {t === 'overdue' ? T.overdue : T.soon(days)}
        </MenuChoice>
      ))}
    </div>
  );
}

/** Isi tabung: all, full or empty (what it held when it last departed; a registered cylinder is empty). */
export function FillPanel({ value, onPick }: { value: FillState | ''; onPick: (v: FillState | '') => void }) {
  return (
    <div className="fmenu-list" role="radiogroup" aria-label={T.fill}>
      <MenuChoice checked={!value} onSelect={() => onPick('')}>
        {T.allFill}
      </MenuChoice>
      {FILL_STATES.map((s) => (
        <MenuChoice key={s} checked={value === s} onSelect={() => onPick(s)}>
          {FILL_STATE_LABEL[s]}
        </MenuChoice>
      ))}
      <p className="fmenu-state xs">{T.fillHelp}</p>
    </div>
  );
}

/** Terakhir terbaca: not read by any Reader for more than 7 / 30 / 90 days, or never. */
export function SeenPanel({ value, onPick }: { value: NotSeenFor | ''; onPick: (v: NotSeenFor | '') => void }) {
  return (
    <div className="fmenu-list" role="radiogroup" aria-label={T.seen}>
      <MenuChoice checked={!value} onSelect={() => onPick('')}>
        {T.allSeen}
      </MenuChoice>
      {NOT_SEEN_FOR.map((s) => (
        <MenuChoice key={s} checked={value === s} onSelect={() => onPick(s)}>
          {T.seenOption[s]}
        </MenuChoice>
      ))}
      <p className="fmenu-state xs">{T.seenActiveOnly}</p>
    </div>
  );
}
